import { useState } from 'react'
import { Crosshair, ExternalLink, MapPin, Pencil } from 'lucide-react'
import { EmptyState, ErrorState, FormDialog, PermissionGate, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { toApiProblem } from '@/lib/api/client'
import { useGeofenceLocations, useGeofenceMutations } from '../../api/geofence'
import { mapLink } from '../../types/geofence'
import type { LocationFenceView } from '../../types/geofence'
import { currentPosition, formatMetres, parseCoordinates } from './geo'

const RADIUS_PRESETS = [50, 100, 200, 500, 1000]

function FenceDialog({ site, onClose }: { site: LocationFenceView; onClose: () => void }) {
  const { saveFence } = useGeofenceMutations()
  const [latitude, setLatitude] = useState(site.latitude?.toString() ?? '')
  const [longitude, setLongitude] = useState(site.longitude?.toString() ?? '')
  const [radius, setRadius] = useState(site.radiusMetres?.toString() ?? '100')
  const [paste, setPaste] = useState('')
  const [locating, setLocating] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const lat = latitude === '' ? null : Number(latitude)
  const lng = longitude === '' ? null : Number(longitude)
  const hasPoint = lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)

  async function locateHere() {
    setError(null)
    setLocating(true)
    try {
      const here = await currentPosition()
      setLatitude(String(here.latitude))
      setLongitude(String(here.longitude))
      setNote(`Your position, accurate to about ${here.accuracy} m. Stand at the middle of the site for the best result.`)
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setLocating(false)
    }
  }

  function applyPaste(text: string) {
    setPaste(text)
    const parsed = parseCoordinates(text)
    if (parsed) {
      setLatitude(String(parsed.latitude))
      setLongitude(String(parsed.longitude))
      setNote('Coordinates read from what you pasted.')
    }
  }

  function save(clear = false) {
    setError(null)
    saveFence.mutate(
      clear
        ? { id: site.id, latitude: null, longitude: null, radiusMetres: null }
        : { id: site.id, latitude: lat, longitude: lng, radiusMetres: radius === '' ? null : Number(radius) },
      {
        onSuccess: onClose,
        onError: (caught) => setError(toApiProblem(caught)?.detail ?? 'The geofence could not be saved.'),
      },
    )
  }

  return (
    <FormDialog
      open
      onOpenChange={(open) => { if (!open) onClose() }}
      title={`Geofence — ${site.name}`}
      description="The centre of the site and how far from it a punch still counts as being there."
      submitLabel="Save geofence"
      loading={saveFence.isPending}
      error={error}
      onSubmit={() => save()}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => { void locateHere() }} disabled={locating}>
            <Crosshair className="size-4" aria-hidden="true" />
            {locating ? 'Finding you…' : 'Use my current location'}
          </Button>
          {hasPoint && (
            <Button type="button" variant="ghost" asChild>
              <a href={mapLink(lat, lng)} target="_blank" rel="noreferrer">
                <ExternalLink className="size-4" aria-hidden="true" />
                Check on Google Maps
              </a>
            </Button>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="fence-paste">Or paste coordinates</Label>
          <Input id="fence-paste" value={paste} placeholder="25.197200, 55.274400 — right-click a point in Google Maps to copy it"
            onChange={(event) => applyPaste(event.target.value)} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="fence-lat">Latitude</Label>
            <Input id="fence-lat" type="number" step="0.0000001" min={-90} max={90}
              value={latitude} onChange={(event) => setLatitude(event.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fence-lng">Longitude</Label>
            <Input id="fence-lng" type="number" step="0.0000001" min={-180} max={180}
              value={longitude} onChange={(event) => setLongitude(event.target.value)} />
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="fence-radius">Radius (metres)</Label>
          <Input id="fence-radius" type="number" min={10} max={50000}
            value={radius} onChange={(event) => setRadius(event.target.value)} />
          <div className="flex flex-wrap gap-1 pt-1">
            {RADIUS_PRESETS.map((preset) => (
              <button key={preset} type="button"
                className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-xs hover:bg-[var(--color-page)]"
                onClick={() => setRadius(String(preset))}>
                {formatMetres(preset)}
              </button>
            ))}
          </div>
          <p className="text-xs text-[var(--color-muted)]">
            100–200 m suits an office building; a campus, port or construction site needs more. Phones in
            buildings are often 20–50 m off, so a very small radius turns people away at the door.
          </p>
        </div>

        {note && <p className="text-xs text-[var(--color-muted)]">{note}</p>}

        {site.fenced && (
          <button type="button" className="text-sm text-[var(--color-danger)] hover:underline"
            onClick={() => save(true)}>
            Remove this geofence
          </button>
        )}
      </div>
    </FormDialog>
  )
}

/** Every site, with its fence or the lack of one. */
export function SitesTab() {
  const sites = useGeofenceLocations()
  const [editing, setEditing] = useState<LocationFenceView | null>(null)

  if (sites.isPending) return <Skeleton className="h-48" />
  if (sites.isError) return <ErrorState error={sites.error} onRetry={() => void sites.refetch()} />
  if (sites.data.length === 0) {
    return <EmptyState icon={MapPin} title="No locations yet"
      description="Add locations under Employees → Org masters, then give each one a geofence here." />
  }

  return (
    <Card className="overflow-x-auto p-0">
      {editing && <FenceDialog site={editing} onClose={() => setEditing(null)} />}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--color-border)] text-xs text-[var(--color-muted)]">
            <th className="p-3 text-start font-medium">Site</th>
            <th className="p-3 text-start font-medium">People today</th>
            <th className="p-3 text-start font-medium">Geofence</th>
            <th className="p-3 text-start font-medium">Centre</th>
            <th className="p-3 text-start font-medium">Radius</th>
            <th className="p-3" />
          </tr>
        </thead>
        <tbody>
          {sites.data.map((site) => (
            <tr key={site.id} className="border-b border-[var(--color-border)] last:border-0">
              <td className="p-3">
                <p className="font-medium text-[var(--color-heading)]">{site.name}</p>
                <p className="text-xs text-[var(--color-muted)]">{site.code}{site.city ? ` · ${site.city}` : ''}</p>
              </td>
              <td className="p-3 tabular-nums">{site.headcount}</td>
              <td className="p-3">
                <StatusBadge status={site.fenced ? 'ACTIVE' : 'NONE'}
                  tone={site.fenced ? 'success' : site.active ? 'warning' : 'neutral'}
                  label={site.fenced ? 'Set' : 'Not set'} />
              </td>
              <td className="p-3 text-xs tabular-nums">
                {site.latitude != null && site.longitude != null
                  ? (
                    <a href={mapLink(site.latitude, site.longitude)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline">
                      {site.latitude.toFixed(5)}, {site.longitude.toFixed(5)}
                      <ExternalLink className="size-3" aria-hidden="true" />
                    </a>
                  )
                  : '—'}
              </td>
              <td className="p-3 tabular-nums">{formatMetres(site.radiusMetres)}</td>
              <td className="p-3 text-end">
                <PermissionGate permission="attendance:write">
                  <Button type="button" size="sm" variant="outline" onClick={() => setEditing(site)}>
                    <Pencil className="size-4" aria-hidden="true" />
                    {site.fenced ? 'Edit' : 'Set geofence'}
                  </Button>
                </PermissionGate>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}
